import {
  STATUS_LABELS,
  STATUS_ORDER,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  SIZE_LABEL,
  SIZE_ORDER,
  MOSCOW_LABELS,
  MOSCOW_ORDER,
} from './types';
import type { IssueStatus, IssuePriority, IssueSize, MoscowLevel } from './types';
import { parseWhen } from './when';

/**
 * "THAT LOOKS LIKE A CARD FIELD, NOT AN EPIC/AREA."
 *
 * The Epic and Area fields are free-text, so a person reaching for somewhere to
 * record "In Progress", "Bug", "High" or "next Friday" can type it there — and
 * then the real field the card already has for that (Status, Type, Size, Priority,
 * the dates) goes unused. This classifier spots when the value being typed IS one
 * of those field's values, so the field can nudge the person onto the real control
 * instead (Vinnie, 20 Oct — a warn + a one-click move, never a block).
 *
 * MATCHING, BY FIELD (Vinnie, 20–21 Oct):
 *  - Status / Priority / Size / Release necessity (MoSCoW) match EXACTLY on their
 *    own label (whole string, case-insensitive). These are short, common words that
 *    legitimately appear inside real epic/area names — "Critical path", "High
 *    availability", "Done criteria", "Could we migrate?" — so a substring or
 *    word-boundary match would fire the nudge on all of those. Only a value that IS
 *    the field word flags.
 *  - A board TYPE (Bug, Feature, …) matches EXACTLY or as a WHOLE WORD inside a
 *    longer value, so "New Feature" and "Feature flags" both flag the type "Feature".
 *    A type name is distinctive enough that catching it inside a longer epic/area is
 *    a help rather than noise; the whole-word rule still keeps "Features" (plural)
 *    and "Featureflag" from matching "Feature".
 *  - ALIASES — the words people actually type instead of the official label ("ASAP"
 *    for Critical, "nice to have" for Could-have, "quick win" for a small size, "P1")
 *    — match the WHOLE string only (never a substring/word-boundary), so "ASAP" flags
 *    but "Urgent care platform" and "Small wins backlog" do not. This is how we
 *    "account for anything close" WITHOUT edit-distance: a curated list, not a guess.
 * Never fuzzy / edit-distance, and always a warn + a one-click move, never a block.
 */

/** Which card field a typed value turned out to belong to. */
export type MisfiledField = 'status' | 'priority' | 'size' | 'type' | 'moscow' | 'date';

export interface Misfiled {
  /** The card field this value belongs on. */
  field: MisfiledField;
  /** The field's own name, for the nudge copy ("looks like a Status"). */
  fieldLabel: string;
  /** The human label of the matched value ("In Progress"), for the copy. */
  valueLabel: string;
  /**
   * The value to APPLY to that field in one click — a status/priority/size KEY, or
   * the type name. Absent for `date`, which has no single target (Start vs Due), so
   * a date is warn-only.
   */
  apply?: string;
}

const FIELD_LABEL: Record<MisfiledField, string> = {
  status: 'Status',
  priority: 'Priority',
  size: 'Size',
  type: 'Type',
  moscow: 'Release necessity',
  date: 'date',
};

/**
 * ALIASES — the everyday words people type instead of a field's official label.
 * Each maps a set of WHOLE-STRING synonyms to the field key they really mean; the
 * nudge then quotes and applies that field's own label, not the word typed. Kept to
 * the fixed-vocabulary fields (Priority, Size, Release necessity) whose keys are
 * stable — a board's own Type names vary per board, so a type synonym could point at
 * a type the board does not have; those stay matched against the board's real type
 * list (exact / whole-word) instead. Add a synonym here, never widen to substrings.
 */
const ALIASES: { field: 'priority' | 'size' | 'moscow'; apply: string; words: string[] }[] = [
  // Priority — urgency.
  { field: 'priority', apply: 'critical', words: ['asap', 'urgent', 'p0', 'p1', 'blocker', 'blocking', 'top priority', 'highest'] },
  { field: 'priority', apply: 'high', words: ['p2', 'important'] },
  { field: 'priority', apply: 'medium', words: ['p3', 'normal'] },
  { field: 'priority', apply: 'low', words: ['p4', 'minor', 'lowest', 'someday', 'whenever'] },
  // Size — effort.
  { field: 'size', apply: 'quick', words: ['small', 'tiny', 'xs', 'quick win', 'trivial'] },
  { field: 'size', apply: 'medium', words: ['moderate'] },
  { field: 'size', apply: 'long', words: ['large', 'big', 'huge', 'xl', 'xxl'] },
  // Release necessity — MoSCoW, in the words people reach for.
  { field: 'moscow', apply: 'must', words: ['must', 'must have', 'must-have', 'required', 'mandatory', 'committed'] },
  { field: 'moscow', apply: 'should', words: ['should', 'should have', 'should-have'] },
  { field: 'moscow', apply: 'could', words: ['could', 'could have', 'could-have', 'nice to have', 'nice-to-have', 'optional'] },
  { field: 'moscow', apply: 'wont', words: ["won't", 'wont', "won't have", 'wont have', 'out of scope', 'not now', 'deferred'] },
];

const PRIORITY_LABEL_OF = PRIORITY_LABELS as Record<string, string>;
const SIZE_LABEL_OF = SIZE_LABEL as Record<string, string>;
const MOSCOW_LABEL_OF = MOSCOW_LABELS as Record<string, string>;

/** The human label of an aliased value, for the nudge copy. */
function aliasLabel(field: 'priority' | 'size' | 'moscow', apply: string): string {
  if (field === 'priority') return PRIORITY_LABEL_OF[apply] ?? apply;
  if (field === 'size') return SIZE_LABEL_OF[apply] ?? apply;
  return MOSCOW_LABEL_OF[apply] ?? apply;
}

/** Exact, case-insensitive match of `v` against a value's key and its label. */
function hit(v: string, key: string, label: string): boolean {
  const n = v.trim().toLowerCase();
  return n === key.toLowerCase() || n === label.trim().toLowerCase();
}

/** Escape a string for use as a literal inside a RegExp. */
function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * True when `word` stands as its own whole word (or phrase) somewhere inside `v`,
 * case-insensitive — bounded by the string ends or a non-letter/non-digit, so
 * "New Feature" and "Feature flags" both contain "Feature", while "Features" and
 * "Featureflag" do not. Words shorter than three characters are not matched this
 * way (a one- or two-letter type would otherwise light up almost anything); they
 * still match exactly via `hit`-style equality at the call site.
 */
function containsWord(v: string, word: string): boolean {
  const w = word.trim();
  if (w.length < 3) return false;
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(w)}([^\\p{L}\\p{N}]|$)`, 'iu').test(v);
}

/**
 * The card field a free-text epic/area value really belongs to, or null when it
 * reads as an ordinary name. `types` is the board's own issue-type vocabulary
 * (per-board, e.g. Bug/Feature/Task) — omitted where the caller has none (the
 * Grid cell, a Settings dialog), in which case type-matching is simply skipped.
 */
export function misfiledAs(value: string, types: string[] = []): Misfiled | null {
  const v = value.trim();
  if (!v) return null;

  // Status (backlog, "In Progress", …)
  for (const k of STATUS_ORDER) {
    if (hit(v, k, STATUS_LABELS[k as IssueStatus])) {
      return { field: 'status', fieldLabel: FIELD_LABEL.status, valueLabel: STATUS_LABELS[k as IssueStatus], apply: k };
    }
  }
  // Priority (Critical, High, Medium, Low)
  for (const k of PRIORITY_ORDER) {
    if (hit(v, k, PRIORITY_LABELS[k as IssuePriority])) {
      return { field: 'priority', fieldLabel: FIELD_LABEL.priority, valueLabel: PRIORITY_LABELS[k as IssuePriority], apply: k };
    }
  }
  // Size (quick / "A few days" / long)
  for (const k of SIZE_ORDER) {
    if (hit(v, k, SIZE_LABEL[k as IssueSize])) {
      return { field: 'size', fieldLabel: FIELD_LABEL.size, valueLabel: SIZE_LABEL[k as IssueSize], apply: k };
    }
  }
  // Release necessity (MoSCoW — Must have / Should have / Could have / Won't have).
  // Exact on the label, like the three above: "could" and "should" are ordinary
  // words that sit inside real names ("Could we migrate?"), so only the whole label.
  for (const k of MOSCOW_ORDER) {
    if (hit(v, k, MOSCOW_LABELS[k as MoscowLevel])) {
      return { field: 'moscow', fieldLabel: FIELD_LABEL.moscow, valueLabel: MOSCOW_LABELS[k as MoscowLevel], apply: k };
    }
  }
  // Board's own types (Bug, Feature, …) — only when the caller supplies them.
  // Matched EXACTLY or as a whole word inside a longer value ("New Feature" →
  // Feature), because a type name is distinctive; the Status/Priority/Size words
  // above stay exact to avoid firing on ordinary names (Vinnie, 20 Oct). The
  // nudge quotes the matched TYPE and applies that type, so "New Feature" reads
  // "'Feature' looks like a Type … set the Type to Feature".
  for (const t of types) {
    const name = t.trim();
    if (name && (v.toLowerCase() === name.toLowerCase() || containsWord(v, name))) {
      return { field: 'type', fieldLabel: FIELD_LABEL.type, valueLabel: name, apply: name };
    }
  }
  // Everyday ALIASES (ASAP, P1, nice to have, quick win, …) — whole string only, so
  // a synonym fires alone but never inside a longer name. Quoted and applied as the
  // field's own label (typing "ASAP" offers "Set the Priority to Critical").
  const n = v.toLowerCase();
  for (const a of ALIASES) {
    if (a.words.some((w) => n === w)) {
      return { field: a.field, fieldLabel: FIELD_LABEL[a.field], valueLabel: aliasLabel(a.field, a.apply), apply: a.apply };
    }
  }
  // A date ("next Friday", "2026-09-18", "today") — warn-only, no single target.
  if (parseWhen(v)) {
    return { field: 'date', fieldLabel: FIELD_LABEL.date, valueLabel: v };
  }
  return null;
}
